<template>
  <section
    v-if="catalog"
    id="reward-catalog"
  >
    <div>
      <catalog-search
        ref="catalogSearch"
        v-intersect="{
          handler: onIntersect,
          options: {
            rootMargin: catalogSearchRootMargin
          }
        }"
        class="pt-8"
      />
      <p
        v-if="noSearchResults"
        class="blue-grey--text mt-4"
      >
        {{ $t('catalog-no-results-found') }}
      </p>
    </div>

    <reward-card-grid
      v-if="hasCashItems"
      :data-array="cashBrandGrid"
      :section-title="$t('catalog-cash')"
      :section-description="$t('catalog-cash-description')"
      :selected="selected"
      @brand-clicked="brandClicked"
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-leave="afterLeave"
      @close="closeRewardDetailView"
    />

    <reward-card-grid
      v-if="hasRewardItems"
      :data-array="eCardBrandGrid"
      :section-title="$t('catalog-eCards')"
      :section-description="$t('catalog-eCards-description')"
      :selected="selected"
      :number-of-e-card-rows="cashBrandGrid.length"
      @brand-clicked="brandClicked"
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-leave="afterLeave"
      @close="closeRewardDetailView"
    />

    <reward-card-grid
      v-if="hasDonationItems"
      :data-array="donationBrandGrid"
      :section-title="$t('catalog-donate')"
      :section-description="$t('catalog-donate-description')"
      :selected="selected"
      :number-of-e-card-rows="eCardBrandGrid.length + cashBrandGrid.length"
      @brand-clicked="brandClicked"
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-leave="afterLeave"
      @close="closeRewardDetailView"
    />
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  GET_CATALOG,
  GET_CATALOG_SEARCH_PARAMS,
  GET_REWARD_CUSTOMIZATION,
  GET_REWARD_LINK_BALANCE
} from '@/store/get-types'
import { APP_SET_DISCLAIMER, SET_SEARCH_IS_VISIBLE } from '@/store/action-types'
import _ from 'lodash'
import { AdCampaignMixin } from '@/components/shared/ad-campaign/AdCampaignMixin'
import { REWARD_LINK_CATALOG_PAGE_CAMPAIGN_KEY } from '@/components/shared/ada/ada-container'
import RewardCardGrid from '@/components/shared/reward-card-grid/RewardCardGrid'
import { scrollTo } from 'scroll-js'
import { getHeightOfElement, getOffsetOfElement } from '@/components/shared/dom/element-positioning'
import { CATALOG_ITEM_SELECTED } from '@/plugins/segment/SegmentConstants'
import CatalogSearch from '@/components/shared/catalog/CatalogSearch'
import { ItemRewardType } from '@/cowboy/EnumTypes'
import {
  ECARD_REWARD_TYPES,
  PAYMENT_REWARD_TYPES
} from '@/components/shared/item/reward-type-constants'
import { RX_ADA_PROACTIVE } from '@/plugins/split/ActiveSplits'
import { HEADER_HEIGHT, HEADER_HEIGHT_SM } from '@/components/header.constants'

const SCROLL_TO_REWARD_DURATION_IN_MILLISECONDS = 1000

export default {
  name: 'CatalogView',
  components: {
    RewardCardGrid,
    CatalogSearch
  },
  mixins: [AdCampaignMixin],
  data () {
    return {
      selected: {
        brand: null,
        row: null,
        trianglePosition: null
      },
      pendingSelected: {
        brand: null,
        row: null,
        trianglePosition: null
      },
      animating: false,
      popupItem: null,
      isScrolling: false,
      allBrandsSorted: [],
      width: 1200
    }
  },
  computed: {
    ...mapGetters({
      rewardCustomization: GET_REWARD_CUSTOMIZATION,
      catalog: GET_CATALOG,
      catalogSearchParams: GET_CATALOG_SEARCH_PARAMS,
      getBalance: GET_REWARD_LINK_BALANCE
    }),
    chunkSize () {
      const windowWidth = this.width
      const { xs, sm, md } = this.$vuetify.breakpoint.thresholds
      switch (true) {
        case windowWidth >= sm && windowWidth < md:
          return 3
        case windowWidth >= xs && windowWidth < sm:
          return 2
        case windowWidth < xs:
          return 1
        default:
          return 4
      }
    },
    filteredBrands () {
      const urlSearchParams = new URLSearchParams('&' + this.catalogSearchParams)
      const { search, fulfillmentTypes } = Object.fromEntries(urlSearchParams.entries())

      let filteredList = _.cloneDeep(this.allBrandsSorted)
      if (search) {
        filteredList = filteredList.filter(brand => brand.displayName.toLowerCase().includes(search.toLowerCase()))
      }
      if (fulfillmentTypes) {
        filteredList = filteredList.filter(brand => {
          brand.items = brand.items.filter(({ fulfillmentType }) => fulfillmentType.toLowerCase() === fulfillmentTypes.toLowerCase())
          return brand.items.length > 0
        })
      }

      return filteredList
    },
    eCardBrandGrid: function () {
      const brandsThatHaveECards = this.filteredBrands.filter(this.brandHasAnECardItem)
      return this.sortAndChunkBrands(brandsThatHaveECards, this.chunkSize)
    },
    cashBrandGrid () {
      const brandHasACashItem = this.filteredBrands.filter(this.brandHasACashItem)
      return this.sortAndChunkBrands(brandHasACashItem, this.chunkSize)
    },
    donationBrandGrid () {
      const brandsThatHaveDonations = this.filteredBrands.filter(this.brandHasADonationItem)
      return this.sortAndChunkBrands(brandsThatHaveDonations, this.chunkSize)
    },
    hasCashItems () {
      return this.cashBrandGrid && this.cashBrandGrid.length
    },
    hasDonationItems () {
      return this.donationBrandGrid && this.donationBrandGrid.length
    },
    hasRewardItems () {
      return this.eCardBrandGrid && this.eCardBrandGrid.length
    },
    noSearchResults () {
      return this.filteredBrands?.length <= 0
    },
    catalogSearchRootMargin () {
      // https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/rootMargin
      const marginTop = this.$vuetify.breakpoint.smAndDown
        ? HEADER_HEIGHT_SM
        : HEADER_HEIGHT
      return `-${marginTop}px 0px 0px 0px`
    }
  },
  watch: {
    catalogSearchParams (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.closeRewardDetailView()
      }
      this.scrollToSearchResults()
    }
  },
  mounted () {
    if (this.$isSplitEnabled(RX_ADA_PROACTIVE) && Number(this.getBalance) === 0) {
      this.$adaChatbot.triggerCampaign(REWARD_LINK_CATALOG_PAGE_CAMPAIGN_KEY)
    }
    if (this.catalog) {
      this.allBrandsSorted = this.checkAndSortBrandsByCustomizedDisplayOrder(this.catalog.brands)
      this.setAlwaysShowDisclaimers(this.catalog.brands) // RL-2383 :: why is this here! I feel like we should do this after we fetch the catalog
      window.onresize = () => {
        this.width = window.innerWidth
      }
      this.width = window.innerWidth
    }
    if (this.hasCashItems) {
      document.title = `Disbursement Link | ${this.$t('catalog-title-default')}`
    }
    // RL-2383 :: Okay, this is a crazy piece of code and should be replaced with a better approach
    // this is triggered when clicking the edit button in the CardMenu component
    // this is passes down the grid to row to details then passed to the Vuex store
    // to be bulled by the cart menu to only be sent back here when you click the edit button
    // NOTE : the itemEditData in the CartItem Object is the eventData object passed to this listener
    this.$root.$on('set-selected-from-cart', eventData => {
      this.determineSelectedAndScrollBehavior(this.selected, eventData)
    })
    // popupItem is required for the v-clickoutside.
    // it's a reference to this component so we don't run the handler when clicking on this component
    this.popupItem = this.$el
  },
  destroyed () {
    this.$root.$off('set-selected-from-cart')
  },
  methods: {
    ...mapActions({
      setSearchIsVisible: SET_SEARCH_IS_VISIBLE,
      addDisclaimer: APP_SET_DISCLAIMER
    }),
    sortAndChunkBrands (brands, chunkSize) {
      return _.chunk(brands, chunkSize)
    },
    brandHasACashItem (brand) {
      return brand.items.some(
        item =>
          item.rewardType &&
          PAYMENT_REWARD_TYPES.includes(item.rewardType.description)
      )
    },
    brandHasAnECardItem (brand) {
      return brand.items.some(
        item =>
          item.rewardType &&
          ECARD_REWARD_TYPES.includes(item.rewardType.description)
      )
    },
    brandHasADonationItem (brand) {
      return brand.items.some(
        item =>
          item.rewardType &&
          item.rewardType.description === ItemRewardType.DONATION
      )
    },
    brandClicked (brand, row, trianglePosition) {
      // allows the animation to finish before a user can click on another brand
      if (this.animating) {
        return
      }

      if (window.innerWidth <= 576) {
        this.$router.push({
          name: 'Reward',
          params: {
            brandKey: brand.brandKey,
            brand: brand,
            id: 'container-reward-details',
            visible: true,
            trianglePosition: ''
          }
        })
      } else {
        this.determineSelectedAndScrollBehavior(this.selected, {
          brand,
          row,
          trianglePosition
        })
      }
      this.$segment.track(CATALOG_ITEM_SELECTED, {
        brandKey: brand.brandKey,
        row: row
      })
      this.$announcer.set(this.$tc('brand-reward-dialog-opened'))
    },
    determineSelectedAndScrollBehavior (previousSelected, newSelected) {
      const noPreviousSelectedBrand = !previousSelected.brand
      const newBrandIsOnTheSameRowAsPreviousBrand =
        previousSelected.brand && previousSelected.row === newSelected.row
      if (noPreviousSelectedBrand || newBrandIsOnTheSameRowAsPreviousBrand) {
        // because there is no previously selected brand or it's on the same row, just scroll to the new brand
        this.setBrand(newSelected)
        if (!noPreviousSelectedBrand) {
          this.scrollToCurrentBrand()
        }
      } else {
        // if there is a previous brand or it's not on the same row, close it and set a pending selected that will be set once the transition finishes
        this.pendingSelected = newSelected
        this.closeRewardDetailView()
      }
    },
    setBrand (selectedBrand) {
      this.selected = selectedBrand
      return this.$nextTick()
    },
    async scrollToCurrentBrand (injectedHeight) {
      if (this.selected) {
        const detailsContainer = document.getElementById(
          `container-reward-details-${this.selected.row}`
        )
        const windowHeight = window.innerHeight
        const headerHeight = getHeightOfElement(
          document.getElementById('svp-head')
        )

        const offsetTopOfDetailsContainer = getOffsetOfElement(detailsContainer)
          .top
        const detailsTopOffset = offsetTopOfDetailsContainer - headerHeight
        const detailsHeight = injectedHeight
          ? Number(injectedHeight.replace('px', ''))
          : getHeightOfElement(detailsContainer)

        const rewardRow = document.getElementById(
          `reward-row-${this.selected.row}`
        )
        const rewardRowHeight = getHeightOfElement(rewardRow)
        const rewardTopOffset = getOffsetOfElement(rewardRow).top - headerHeight

        const halfOfPageWhitespace =
          (windowHeight - headerHeight - detailsHeight - rewardRowHeight) / 2 -
          8
        const positionToScrollTo =
          halfOfPageWhitespace < 0
            ? detailsTopOffset
            : rewardTopOffset - halfOfPageWhitespace

        const currentScrollTop = document.documentElement.scrollTop
        if (
          !this.isScrolling &&
          Number(currentScrollTop) !== Number(positionToScrollTo)
        ) {
          this.isScrolling = true
          await scrollTo(document.documentElement, {
            top: positionToScrollTo,
            duration: SCROLL_TO_REWARD_DURATION_IN_MILLISECONDS
          })
          this.isScrolling = false
        }
      }
    },
    closeRewardDetailView () {
      this.selected = { brand: null, row: null, trianglePosition: null }
    },
    checkAndSortBrandsByCustomizedDisplayOrder (brands) {
      const sortedBrands = [...brands]
      const customDisplayOrder = this.rewardCustomization?.details?.customBrandDisplayOrder
      if (Array.isArray(customDisplayOrder) && customDisplayOrder.length > 0) {
        const utidCustomDisplayOrder = customDisplayOrder.map(brand => brand.brandID).reverse()
        sortedBrands.sort((a, b) => utidCustomDisplayOrder.indexOf(b.brandID) - utidCustomDisplayOrder.indexOf(a.brandID))
      }

      const adCampaign = this.rewardCustomization?.adCampaign
      const adAssociatedBrands = adCampaign?.adAssociatedBrands
      if (adCampaign && this.$_adCampaignMixin_adCampaignIsActive(adCampaign) &&
        Array.isArray(adAssociatedBrands) && adAssociatedBrands.length > 0) {
        const adCampaignCustomDisplayOrder = adAssociatedBrands.sort((a, b) => b.sortOrder - a.sortOrder).map(brand => brand.coreBrandId)
        sortedBrands.sort((a, b) => adCampaignCustomDisplayOrder.indexOf(b.brandID) - adCampaignCustomDisplayOrder.indexOf(a.brandID))
      }
      return sortedBrands
    },
    setAlwaysShowDisclaimers (brands) {
      if (brands && Array.isArray(brands)) {
        brands.forEach(brand => {
          if (brand.alwaysShowDisclaimer) {
            this.addDisclaimer(brand.disclaimer)
          }
        })
      }
    },
    beforeEnter () {
      this.animating = true
    },
    enter (element) {
      // "What madness is this?!" I'm sure you're asking. Good question!
      // https://markus.oberlehner.net/blog/transition-to-height-auto-with-vue/
      // Animations require fixed heights. Sooooo...We ask the browser nicely to
      // tell us what that height would be! Which it does! Hooray! :D
      element.style.width = getComputedStyle(element).width
      element.style.position = 'absolute'
      element.style.visibility = 'hidden'
      element.style.height = 'auto'

      const height = getComputedStyle(element).height
      element.style.width = ''
      element.style.position = ''
      element.style.visibility = ''
      element.style.height = 0

      // Then, we set the height "sometime else" to trigger the animation
      setTimeout(() => {
        element.style.height = height
      })

      // Then we scroll to the thing!
      this.$nextTick().then(() => {
        this.scrollToCurrentBrand(height)
      })
    },
    afterEnter (element) {
      this.animating = false
      element.style.height = 'auto'
    },
    beforeLeave () {
      this.animating = true
    },
    leave (element) {
      element.style.height = getComputedStyle(element).height

      // Force repaint to make sure the
      // animation is triggered correctly.
      // https://markus.oberlehner.net/blog/transition-to-height-auto-with-vue/
      // eslint-disable-next-line no-unused-expressions
      getComputedStyle(element).height

      setTimeout(() => {
        element.style.height = 0
      })
    },
    afterLeave () {
      this.animating = false
      if (this.pendingSelected.brand) {
        // if the reward detail view is closed and switching to a new brand, set that new brand as
        // the main selected and scroll to it
        const brandToSetAndScrollTo = _.cloneDeep(this.pendingSelected)
        this.pendingSelected = {
          brand: null,
          row: null,
          trianglePosition: null
        }
        this.setBrand(brandToSetAndScrollTo)
      }
    },
    onIntersect (entries) {
      if (entries && entries.length) {
        this.setSearchIsVisible(entries[0].isIntersecting)
      }
    },
    scrollToSearchResults () {
      const { offsetTop = 0, offsetHeight = 0 } = this.$refs?.catalogSearch?.$el
      const bringSearchIntoFrame = offsetTop - offsetHeight
      if (window.scrollY !== bringSearchIntoFrame) {
        window.scrollTo(0, bringSearchIntoFrame)
      }
    }
  }
}
</script>
